import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';

import { cn } from '../../utils/cn';
import { DialogOverlay } from './dialog-overlay';
import { DialogPortal } from './dialog-portal';
import type { DialogCloseVariant } from './dialog-close';
import { DialogClose } from './dialog-close';

const dialogContentVariants = cva('rocco-dialog-content z-modal', {
    variants: {
        variant: {
            default:
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 sm:rounded-lg',
            fullScreen:
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed inset-0 grid gap-4 border bg-background shadow-lg duration-200 sm:rounded-lg',
            pdfViewer:
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed inset-0 grid gap-4 bg-[#3D3938] duration-200',
            hde: 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-none border bg-background shadow-lg duration-200 md:rounded-[16px]',
        },
        spacing: {
            none: 'p-0',
            sm: 'p-4',
            md: 'p-6',
            lg: 'p-8',
            xl: 'p-10',
            '2xl': 'p-12',
            '3xl': 'p-14',
            '4xl': 'p-16',
        },
    },
    defaultVariants: {
        variant: 'default',
        spacing: 'md',
    },
});

const hideCloseButton = (
    variant: 'default' | 'fullScreen' | 'pdfViewer' | 'hde' | null | undefined,
    closeButton?: boolean,
) => {
    // If closeButton is explicitly set to false, hide the button
    if (closeButton === false) {
        return true;
    }
    // Otherwise, use the variant-based logic
    return variant === 'pdfViewer';
};

/** Content dialog variant options */
type DialogContentVariant = 'default' | 'fullScreen' | 'pdfViewer' | 'hde';

/** Content spacing options */
type DialogContentSpacing =
    | 'none'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl';

interface DialogContentProps extends React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
> {
    overlayClassName?: string;
    closeButton?: boolean;
    /** The visual style variant of the dialog content */
    variant?: DialogContentVariant | null;
    /** The padding/spacing of the dialog content */
    spacing?: DialogContentSpacing | null;
    /** Variant style for the close button */
    closeButtonVariant?: DialogCloseVariant;
    closeButtonClassName?: string;
}

export const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    DialogContentProps
>(
    (
        {
            className,
            children,
            closeButtonClassName,
            overlayClassName,
            variant,
            spacing,
            closeButton,
            closeButtonVariant,
            ...props
        },
        ref,
    ) => {
        return (
            <DialogPortal>
                <DialogOverlay className={overlayClassName} />
                <DialogPrimitive.Content
                    ref={ref}
                    className={cn(
                        dialogContentVariants({ variant, spacing }),
                        className,
                    )}
                    {...props}
                >
                    {children}
                    {!hideCloseButton(variant, closeButton) && (
                        <DialogClose
                            variant={closeButtonVariant}
                            className={closeButtonClassName}
                        />
                    )}
                </DialogPrimitive.Content>
            </DialogPortal>
        );
    },
);

DialogContent.displayName = DialogPrimitive.Content.displayName;
